// Sparkline.jsx (upgraded: axes + hover tooltip + crosshair)
// Backwards compatible: you can still pass values=[...]
// For x-axis time labels + hover timestamp: pass `data=[{ t: <ms>, v: <number> }, ...]`
// If you only pass values, it will auto-generate timestamps by index (no real time labels).

import React, { useMemo, useRef, useState } from "react";

export default function Sparkline({
  values = [],
  data = null, // ✅ new preferred input: [{t, v}]
  height = 140, // more "chart-like" for axes
  zones = [],
  format = (v) => String(v),
  xFormat = (t) => {
    // default HH:MM:SS
    const d = new Date(t);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  },
  yLabel = "", // optional label e.g. "ms"
}) {
  const w = 520; // bigger canvas = looks more pro inside your card
  const h = height;

  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null); // { i, x, y, v, t }

  const series = useMemo(() => {
    if (Array.isArray(data) && data.length) {
      return data
        .map((p) => ({
          t: Number(p?.t ?? 0),
          v: Number(p?.v ?? 0),
        }))
        .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v));
    }
    // fallback: values only (no real time axis)
    return (values || []).map((v, i) => ({
      t: i,
      v: Number(v || 0),
    }));
  }, [data, values]);

  const pts = series.map((p) => p.v);
  const ts = series.map((p) => p.t);

  const has = series.length >= 2;

  // Padding tuned for axes + labels
  const padL = 44; // left axis labels
  const padR = 16;
  const padT = 12;
  const padB = 26; // x axis labels
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  const min = has ? Math.min(...pts) : 0;
  const max = has ? Math.max(...pts) : 1;

  // add a bit of headroom (nicer chart)
  const yMin = min;
  const yMax = max === min ? min + 1 : max;
  const yPad = (yMax - yMin) * 0.08;
  const y0 = yMin;
  const y1 = yMax + (yPad || 0);

  const xMin = has ? Math.min(...ts) : 0;
  const xMax = has ? Math.max(...ts) : 1;

  const toX = (t) => {
    const den = xMax - xMin || 1;
    const u = (t - xMin) / den;
    return padL + u * innerW;
  };

  const toY = (v) => {
    const den = y1 - y0 || 1;
    const u = (v - y0) / den;
    return padT + (1 - u) * innerH;
  };

  const d = has
    ? series
        .map((p, i) => `${i === 0 ? "M" : "L"} ${toX(p.t).toFixed(2)} ${toY(p.v).toFixed(2)}`)
        .join(" ")
    : "";

  // y ticks (3 lines)
  const yTicks = useMemo(() => {
    const ticks = 3;
    const out = [];
    for (let i = 0; i < ticks; i++) {
      const t = i / (ticks - 1);
      const v = y0 + (y1 - y0) * (1 - t);
      out.push({ v, y: padT + innerH * t });
    }
    return out;
  }, [y0, y1, innerH]);

  // x ticks (start / mid / end)
  const xTicks = useMemo(() => {
    const ticks = 3;
    const out = [];
    for (let i = 0; i < ticks; i++) {
      const t = i / (ticks - 1);
      const tt = xMin + (xMax - xMin) * t;
      out.push({ t: tt, x: toX(tt) });
    }
    return out;
  }, [xMin, xMax]);

  const zoneRects = has
    ? zones
        .map((z, idx) => {
          const zFrom = Math.max(y0, z.from);
          const zTo = Math.min(y1, z.to);
          if (zTo <= zFrom) return null;

          const yA = toY(zFrom);
          const yB = toY(zTo);
          const y = Math.min(yA, yB);
          const hh = Math.abs(yB - yA);

          return (
            <rect
              key={idx}
              x={padL}
              y={y}
              width={innerW}
              height={hh}
              rx="10"
              fill={z.fill}
              opacity={z.opacity ?? 0.12}
            />
          );
        })
        .filter(Boolean)
    : null;

  const onMove = (e) => {
    if (!has) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left;

    // map mouse x to nearest point
    const u = (mx - padL) / innerW;
    const clamped = Math.max(0, Math.min(1, u));

    const targetT = xMin + (xMax - xMin) * clamped;

    // nearest by x (time)
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < series.length; i++) {
      const dist = Math.abs(series[i].t - targetT);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }

    const p = series[best];
    const x = toX(p.t);
    const y = toY(p.v);

    setHover({ i: best, x, y, v: p.v, t: p.t });
  };

  const onLeave = () => setHover(null);

  // tooltip positioning (within svg viewbox space)
  const tooltipBox = hover
    ? (() => {
        const text1 = xFormat(hover.t);
        const text2 = `${format(hover.v)}${yLabel ? ` ${yLabel}` : ""}`;
        // approximate width; good enough and keeps it simple
        const boxW = Math.max(140, Math.min(220, (text1.length + text2.length) * 6));
        const boxH = 44;

        let x = hover.x + 10;
        let y = hover.y - boxH - 10;

        if (x + boxW > w - 6) x = hover.x - boxW - 10;
        if (y < 6) y = hover.y + 12;

        return { x, y, w: boxW, h: boxH, text1, text2 };
      })()
    : null;

  return (
    <div ref={wrapRef} style={{ width: "100%" }}>
      <svg
        width="100%"
        viewBox={`0 0 ${w} ${h}`}
        style={{ display: "block" }}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        {/* Zones */}
        {zoneRects}

        {has ? (
          <>
            {/* Gridlines (subtle) */}
            {yTicks.map((t, i) => (
              <line
                key={i}
                x1={padL}
                x2={w - padR}
                y1={t.y}
                y2={t.y}
                stroke="rgba(15,23,42,0.066)"
              />
            ))}

            {/* Axes */}
            <line x1={padL} x2={padL} y1={padT} y2={h - padB} stroke="rgba(15,23,42,0.11)" />
            <line x1={padL} x2={w - padR} y1={h - padB} y2={h - padB} stroke="rgba(15,23,42,0.11)" />

            {/* Y labels */}
            {yTicks.map((t, i) => (
              <text
                key={i}
                x={padL - 8}
                y={t.y + 3}
                fontSize="10"
                textAnchor="end"
                fill="#4b5563"
              >
                {format(t.v)}
              </text>
            ))}

            {/* X labels */}
            {xTicks.map((t, i) => (
              <text
                key={i}
                x={t.x}
                y={h - 8}
                fontSize="10"
                textAnchor={i === 0 ? "start" : i === xTicks.length - 1 ? "end" : "middle"}
                fill="#4b5563"
              >
                {xFormat(t.t)}
              </text>
            ))}

            {/* Line */}
            <path d={d} fill="none" stroke="rgba(0,108,253,0.95)" strokeWidth="2.2" />

            {/* Hover crosshair */}
            {hover && (
              <>
                <line
                  x1={hover.x}
                  x2={hover.x}
                  y1={padT}
                  y2={h - padB}
                  stroke="rgba(15,23,42,0.11)"
                  strokeDasharray="3 4"
                />
                <line
                  x1={padL}
                  x2={w - padR}
                  y1={hover.y}
                  y2={hover.y}
                  stroke="rgba(15,23,42,0.11)"
                  strokeDasharray="3 4"
                />
                <circle cx={hover.x} cy={hover.y} r="4.2" fill="rgba(0,108,253,1)" />
              </>
            )}

            {/* Last point */}
            <circle
              cx={toX(series[series.length - 1].t)}
              cy={toY(series[series.length - 1].v)}
              r="3.5"
              fill="rgba(0,108,253,1)"
              opacity={hover ? 0.7 : 1}
            />

            {/* Tooltip */}
            {tooltipBox && (
              <g>
                <rect
                  x={tooltipBox.x}
                  y={tooltipBox.y}
                  width={tooltipBox.w}
                  height={tooltipBox.h}
                  rx="10"
                  fill="#f8f9fb"
                  stroke="rgba(15,23,42,0.12)"
                />
                <text
                  x={tooltipBox.x + 10}
                  y={tooltipBox.y + 18}
                  fontSize="10"
                  fill="#4b5563"
                >
                  {tooltipBox.text1}
                </text>
                <text
                  x={tooltipBox.x + 10}
                  y={tooltipBox.y + 34}
                  fontSize="12"
                  fontWeight="800"
                  fill="#15171c"
                >
                  {tooltipBox.text2}
                </text>
              </g>
            )}
          </>
        ) : (
          <text x={w / 2} y={h / 2} fontSize="10" textAnchor="middle" fill="#4b5563">
            collecting…
          </text>
        )}
      </svg>
    </div>
  );
}
